import * as React from 'react'

import { Input, InputProps } from '@/shared/ui/input/input'
import { Label } from '@/ui/label'
import { cn } from '@/shared/lib/classnames'
import { PasswordInput } from '../password-input'

interface FloatingInputProps extends InputProps {}
type FloatingInputWithRefProps = typeof FloatingInput

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ className, variant, placeholder, ...props }, ref) => {
    const isError = variant === 'destructive'
    return (
      <Input
        placeholder={isError ? placeholder : ''}
        variant={variant}
        className={cn(
          'peer pt-[23px] pb-[5px] focus:pt-[23px] focus:pb-[5px] placeholder-gray-600!',
          isError && 'pt-[23px]! pb-[5px]!',
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
FloatingInput.displayName = 'FloatingInput'

const FloatingPasswordInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ className, variant, placeholder, ...props }, ref) => {
    const isError = variant === 'destructive'
    return (
      <PasswordInput
        placeholder={isError ? placeholder : ''}
        variant={variant}
        className={cn(
          'peer pt-[23px] pb-[5px] focus:pt-[23px] focus:pb-[5px] placeholder-gray-600!',
          isError && 'pt-[23px]! pb-[5px]!',
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)

FloatingPasswordInput.displayName = 'FloatingPasswordInput'

const FloatingLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label> & {
    isError?: boolean
    placeholderShown?: boolean
  }
>(({ className, isError, placeholderShown, ...props }, ref) => {
  return (
    <Label
      className={cn(
        'absolute pointer-events-none start-2 top-[18px] z-10 origin-left -translate-y-4 scale-75 transform bg-background px-[11px] text-sm text-gray-600 font-normal duration-300 truncate max-w-[420px] bg-transparent',
        'peer-focus:top-[18px] peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-[11px]',
        'peer-focus:rtl:left-auto peer-focus:rtl:translate-x-1/4',
        'peer-focus:max-w-[420px] ',
        'peer-focus:secondary peer-focus:dark:secondary',
        placeholderShown && 'max-w-[310px] top-1/2 -translate-y-1/2 scale-100',
        isError && 'top-[18px] scale-75 -translate-y-4 text-destructive max-w-[420px]',
        className,
      )}
      ref={ref}
      {...props}
    />
  )
})
FloatingLabel.displayName = 'FloatingLabel'

type FloatingLabelInputProps = InputProps & {
  label?: string
  errorMessage?: string
}

const FloatingLabelInput = React.forwardRef<
  React.ElementRef<typeof FloatingInput>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(({ id, label, errorMessage, variant, ...props }, ref) => {
  const [value, setValue] = React.useState(props.value)
  React.useEffect(() => {
    setValue(props.value)
  }, [props.value])

  return (
    <div className="relative">
      <FloatingInput
        placeholder={errorMessage ? label : ''}
        ref={ref}
        id={id}
        variant={variant}
        {...props}
        onChange={(e) => {
          setValue(e.target.value)
          props?.onChange && props.onChange(e)
        }}
      />
      <FloatingLabel placeholderShown={!value} htmlFor={id} isError={variant === 'destructive'}>
        {errorMessage ? errorMessage : label}
      </FloatingLabel>
    </div>
  )
})
FloatingLabelInput.displayName = 'FloatingLabelInput'

const FloatingPasswordLabelInput = React.forwardRef<
  React.ElementRef<typeof FloatingInput>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(({ id, label, errorMessage, variant, ...props }, ref) => {
  const [value, setValue] = React.useState('')

  return (
    <div className="relative">
      <FloatingPasswordInput
        placeholder={errorMessage ? label : ''}
        ref={ref}
        id={id}
        variant={variant}
        {...props}
        onChange={(e) => {
          setValue(e.target.value)
          props?.onChange && props.onChange(e)
        }}
      />
      <FloatingLabel placeholderShown={!value} htmlFor={id} isError={variant === 'destructive'}>
        {errorMessage ? errorMessage : label}
      </FloatingLabel>
    </div>
  )
})
FloatingPasswordLabelInput.displayName = 'FloatingPasswordLabelInput'

export { FloatingLabelInput, FloatingPasswordLabelInput }
export type { FloatingLabelInputProps, FloatingInputProps, FloatingInputWithRefProps }
